import { useFundFamilySubEventAdjustmentUpsert } from "gen/endpoints/fund-family-event-admin/fund-family-event-admin";
import { AdjustableFundFamilySubEventTypeEnum } from "gen/model/adjustableFundFamilySubEventTypeEnum";
import { AdjustmentSubEventItemAPIData } from "gen/model/adjustmentSubEventItemAPIData";
import { compact, map, values } from "lodash-es";
import { useCallback } from "react";

// TODO: ENG-8118 Better type inference for form values from backend table
type UniqueInvestorFund = string;
type TransactionCode = string;
type Adjustment = Pick<
  AdjustmentSubEventItemAPIData,
  | "investment_id"
  | "target_amount"
  | "transaction_code_id"
  | "portfolio_transaction_id"
>;

type FormValues = Record<
  UniqueInvestorFund,
  {
    fund_id: string;
    investor_entity_id: string;

    transactionAdjustments: {
      [x: TransactionCode]: Adjustment;
    };
  }
>;

export const useAdjustmentsModal = ({
  subEventTypeBeingAdjusted,
  eventData,
  postSubmit,
}: {
  subEventTypeBeingAdjusted?: AdjustableFundFamilySubEventTypeEnum;
  eventData?: {
    fund_family_id: string;
    id?: string | null;
  };
  postSubmit: () => Promise<void>;
}) => {
  const { mutateAsync: upsertAdjustmentSubEvent, reset } =
    useFundFamilySubEventAdjustmentUpsert();
  const onSubmit = useCallback(
    async (formValues: FormValues) => {
      if (!eventData?.id) throw new Error("Event data is required");
      const adjustments = values(formValues).reduce(
        (acc: AdjustmentSubEventItemAPIData[], rowValue) => {
          const rowAdjustments: AdjustmentSubEventItemAPIData[] = compact(
            map(
              rowValue.transactionAdjustments,
              ({
                target_amount,
                investment_id,
                transaction_code_id,
                portfolio_transaction_id,
              }) => {
                // only add adjustments they actually input
                // Note: "0" is a valid input
                if (!target_amount) {
                  return;
                }
                return {
                  target_amount,
                  fund_id: rowValue.fund_id,
                  investment_id,
                  investor_entity_id: rowValue.investor_entity_id,
                  transaction_code_id,
                  portfolio_transaction_id,
                };
              },
            ),
          );
          return [...acc, ...rowAdjustments];
        },
        [],
      );

      reset();
      await upsertAdjustmentSubEvent({
        fundFamilyId: eventData.fund_family_id,
        fundFamilyEventId: eventData.id,
        data: {
          sub_event_type_being_adjusted: subEventTypeBeingAdjusted,
          adjustments,
        },
      });
      await postSubmit();
    },
    [
      eventData,
      postSubmit,
      reset,
      subEventTypeBeingAdjusted,
      upsertAdjustmentSubEvent,
    ],
  );
  return { onSubmit };
};
